import React, { useCallback, useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import supabase from '../../services/supabaseClient';
import {
  CLASSE_DO_TOM, formatarReais, rotuloDoLancamento, tomDoSaldo,
} from '../../lib/saldoDisparos';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';

const A_CADA_MS = 60_000;
const EXTRATO_LINHAS = 50;

/** Lê o saldo e o aviso da empresa. Sem cobrança ligada (isenta) devolve null. */
async function lerSaldo(tenantId) {
  const { data: assinatura } = await supabase
    .from('assinaturas')
    .select('cobranca_ativa, saldo_aviso_centavos')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (!assinatura?.cobranca_ativa) return null;
  const { data: saldo } = await supabase
    .from('saldo_disparos')
    .select('saldo_centavos')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  const { data: somenteLeitura } = await supabase.rpc('somente_leitura', { p_tenant: tenantId });
  return {
    saldo: Number(saldo?.saldo_centavos ?? 0),
    aviso: assinatura.saldo_aviso_centavos,
    somenteLeitura: somenteLeitura === true,
  };
}

async function lerExtrato(tenantId) {
  const { data } = await supabase
    .from('saldo_lancamentos')
    .select('id, tipo, valor_centavos, saldo_apos, categoria, observacao, criado_em')
    .eq('tenant_id', tenantId)
    .order('criado_em', { ascending: false })
    .limit(EXTRATO_LINHAS);
  return data ?? [];
}

function LinhaDoExtrato({ lancamento }) {
  const valor = Number(lancamento.valor_centavos);
  return (
    <li className="flex items-center justify-between gap-3 py-2 text-sm">
      <div className="min-w-0">
        <p className="font-medium text-slate-800 truncate">{rotuloDoLancamento(lancamento)}</p>
        <p className="text-xs text-slate-500 truncate">
          {new Date(lancamento.criado_em).toLocaleString('pt-BR')}
          {lancamento.observacao ? ` · ${lancamento.observacao}` : ''}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className={valor >= 0 ? 'font-semibold text-emerald-700' : 'font-semibold text-slate-700'}>
          {valor >= 0 ? '+' : '−'}{formatarReais(Math.abs(valor))}
        </p>
        <p className="text-xs text-slate-400">saldo {formatarReais(lancamento.saldo_apos)}</p>
      </div>
    </li>
  );
}

/**
 * O saldo de disparos no topo, com o extrato ao clicar (spec precificacao, R15/R16).
 * Empresa isenta (sem cobrança ligada) não vê nada.
 */
export default function SaldoDisparos({ tenantId }) {
  const [dados, setDados] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [extrato, setExtrato] = useState([]);

  const atualizar = useCallback(async () => {
    if (!tenantId) return;
    setDados(await lerSaldo(tenantId));
  }, [tenantId]);

  useEffect(() => {
    atualizar();
    const timer = setInterval(atualizar, A_CADA_MS);
    window.addEventListener('saldo:atualizar', atualizar);
    return () => {
      clearInterval(timer);
      window.removeEventListener('saldo:atualizar', atualizar);
    };
  }, [atualizar]);

  const abrir = async () => {
    setAberto(true);
    setExtrato(await lerExtrato(tenantId));
    atualizar();
  };

  if (!dados) return null;
  const tom = tomDoSaldo(dados.saldo, dados.aviso);

  return (
    <>
      {dados.somenteLeitura && (
        <span
          role="status"
          className="hidden md:flex items-center h-9 px-3 rounded-full border text-sm font-semibold bg-red-50 text-red-700 border-red-300"
        >
          Teste encerrado: escolha um plano
        </span>
      )}
      <button
        type="button"
        onClick={abrir}
        title="Saldo de disparos — clique para ver o extrato"
        className={`flex items-center gap-1.5 h-9 px-3 rounded-full border text-sm font-semibold ${CLASSE_DO_TOM[tom]}`}
      >
        <Wallet className="h-4 w-4" />
        <span className="hidden sm:inline">Saldo</span> {formatarReais(dados.saldo)}
      </button>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Saldo de disparos: {formatarReais(dados.saldo)}</DialogTitle>
            <DialogDescription>
              Cada mensagem de template enviada desconta do saldo. O atendimento que o cliente
              inicia é grátis. Para recarregar, fale com a IntegrAI.
            </DialogDescription>
          </DialogHeader>
          {tom === 'zerado' && (
            <p className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3">
              Saldo zerado: os disparos estão parados. O atendimento continua normal.
            </p>
          )}
          <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {extrato.length === 0 && <li className="py-6 text-center text-sm text-slate-500">Nenhum lançamento ainda.</li>}
            {extrato.map((l) => <LinhaDoExtrato key={l.id} lancamento={l} />)}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
